"use client";

import { forwardRef, type ButtonHTMLAttributes } from "react";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg" | "icon";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "border-transparent bg-[var(--fz-button-primary-bg)] text-[var(--fz-button-primary-text)] hover:bg-[var(--fz-button-primary-hover)]",
  secondary: "border-transparent bg-[var(--fz-button-secondary-bg)] text-[var(--fz-button-secondary-text)] hover:bg-[var(--fz-button-secondary-hover)]",
  outline: "border-[var(--fz-button-outline-border)] bg-[var(--fz-button-outline-bg)] text-[var(--fz-button-outline-text)] hover:bg-[var(--fz-button-outline-hover)]",
  ghost: "border-transparent bg-transparent text-[var(--fz-button-outline-text)] hover:bg-[var(--fz-button-ghost-hover)]",
  danger: "border-transparent bg-[var(--fz-button-danger-bg)] text-[var(--fz-button-danger-text)] hover:bg-[var(--fz-button-danger-hover)]",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-11 px-5 text-sm",
  icon: "h-10 w-10 p-0 text-sm",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    className = "",
    disabled = false,
    loading = false,
    size = "md",
    type = "button",
    variant = "primary",
    ...props
  },
  ref,
) {
  return (
    <button
      {...props}
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading ? "true" : undefined}
      data-loading={loading ? "true" : undefined}
      className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-[var(--fz-button-radius)] border font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--fz-button-focus-ring)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-[var(--fz-button-disabled-opacity)] ${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]} ${className}`}
    >
      {children}
    </button>
  );
});
